// src/hooks/useStudios.js
// Custom hook for fetching Studios data from Contentful

import { useContentful } from './useContentful';
import { STUDIOS_QUERY, STUDIO_BY_SLUG_QUERY } from '../contentful/studioQueries';
import { mapStudiosList, mapStudio } from '../contentful/studioMappers';

/**
 * Hook for fetching all studios from Contentful
 * Returns studios array with loading and error states
 *
 * @param {object} options - Hook options
 * @param {boolean} options.skip - Skip fetching
 * @returns {object} { studios, loading, error, refetch }
 */
export const useStudios = (options = {}) => {
  const { data, loading, error, refetch } = useContentful(
    STUDIOS_QUERY,
    {},
    {
      mapper: mapStudiosList,
      cacheTime: 5 * 60 * 1000, // 5 minutes
      ...options,
    }
  );

  return {
    studios: data || [],
    loading,
    error,
    refetch,
  };
};

/**
 * Hook for fetching a single studio by slug
 *
 * @param {string} slug - Studio slug
 * @returns {object} { studio, loading, error }
 */
export const useStudio = (slug) => {
  const { data, loading, error } = useContentful(
    STUDIO_BY_SLUG_QUERY,
    { slug },
    {
      skip: !slug,
      mapper: (response) => {
        const entry = response?.studioCollection?.items?.[0];
        return mapStudio(entry);
      },
    }
  );

  return {
    studio: data,
    loading,
    error,
  };
};

export default useStudios;
